import ServicePrice from "./ServicePrice";
import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, User } from "lucide-react";
import { Logo } from "./Logo";
import { useCatalog } from "../contexts/CatalogContext";
import { useCustomer } from "../contexts/CustomerContext";

const navLinks = [
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const { isAuthenticated, customer, logout, requestLogin } = useCustomer();
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const { pathname } = useLocation();
  const { categories: CATEGORIES } = useCatalog();

  const orderedCategories = useMemo(() => {
    const roCategory = CATEGORIES.find((c) => c.id === "ro");
    return roCategory ? [roCategory, ...CATEGORIES.filter((c) => c.id !== "ro")] : CATEGORIES;
  }, [CATEGORIES]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="customer-navbar sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {/* Services dropdown */}
          <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
            <button
              data-testid="nav-services-dropdown"
              onClick={() => setSvcOpen((value) => !value)}
              aria-expanded={svcOpen}
              className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-[#0369A1] transition-colors"
            >
              Services <ChevronDown className="w-4 h-4" />
            </button>
            {svcOpen && (
              <div className="absolute top-full left-0 pt-3 w-64">
                <div className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
                  {orderedCategories.map((c) => {
                    const Icon = c.icon;
                    return (
                      <Link
                        key={c.id}
                        to={`/services?category=${c.id}`}
                        data-testid={`nav-service-${c.id}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.color }}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                          <div className="text-xs text-slate-500">From <ServicePrice service={{ ...c, price: c.startingPrice }} /></div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${pathname === l.to ? "text-[#0369A1]" : "text-slate-700 hover:text-[#0369A1]"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/join-as-technician"
            data-testid="nav-join-technician-btn"
            className="text-sm font-semibold px-4 py-2 rounded-xl border border-[#0369A1] text-[#0369A1] hover:bg-[#0369A1] hover:text-white transition-all"
          >
            Join as Technician
          </Link>

          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="text-sm font-semibold px-4 py-2 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition-all flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                {customer?.name || "Dashboard"}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="text-sm font-semibold px-4 py-2 rounded-xl border border-slate-300 text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                onClick={requestLogin}
                data-testid="nav-login-btn"
                className="text-sm font-semibold px-5 py-2.5 rounded-xl border border-[#0369A1] text-[#0369A1] hover:bg-[#0369A1] hover:text-white transition-all"
              >
                Login
              </button>
              <Link
                to="/booking"
                data-testid="nav-book-now-btn"
                className="text-sm font-semibold px-5 py-2.5 rounded-xl bg-[#0369A1] text-white hover:bg-[#075985] transition-all shadow-sm"
              >
                Book Now
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu */}
        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close navigation" : "Open navigation"}
          aria-expanded={open}
          className="lg:hidden p-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-4 space-y-1">
            {orderedCategories.map((c) => (
              <Link
                key={c.id}
                to={`/services?category=${c.id}`}
                data-testid={`mobile-nav-${c.id}`}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {c.name}
              </Link>
            ))}
            <div className="h-px bg-slate-200 my-2" />
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/join-as-technician"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-join-technician"
              className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Join as Technician
            </Link>
            <div className="h-px bg-slate-200 my-2" />
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-dashboard"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  {customer?.name || "Dashboard"}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setOpen(false);
                  }}
                  data-testid="mobile-nav-logout"
                  className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    requestLogin();
                    setOpen(false);
                  }}
                  data-testid="mobile-nav-login"
                  className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-[#0369A1] hover:bg-cyan-50"
                >
                  Login
                </button>
                <Link
                  to="/booking"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-book-now"
                  className="block text-center mt-3 px-5 py-3 rounded-xl bg-[#0369A1] text-white font-semibold"
                >
                  Book Now
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
